"use client";

import { useState, useRef, useEffect, type ReactNode } from "react";
import { Modal, Spin } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

import { appZIndex } from "../../utils/appconst";
import { IBookCollectionData } from "../../apiservice/userService.type";
import { userGetBookGroups } from "../../apiservice/userService";
import { BookSliderSkeleton } from "../BookComponents/BookSkeletonLoading";

export const HomeCollectionCollections = () => {
  const navigate = useNavigate();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [Collections, setCollections] = useState<IBookCollectionData[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if we need to show navigation arrows
  useEffect(() => {
    const checkScroll = () => {
      if (!sliderRef.current) return;

      setShowLeftArrow(sliderRef.current.scrollLeft > 0);
      setShowRightArrow(
        sliderRef.current.scrollLeft <
          sliderRef.current.scrollWidth - sliderRef.current.clientWidth - 10
      );
    };

    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener("scroll", checkScroll);
      // Initial check
      checkScroll();
    }

    return () => {
      if (slider) {
        slider.removeEventListener("scroll", checkScroll);
      }
    };
  }, [Collections]);

  // Scroll functions
  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  // Show Alert
  const showAlert = (title: string, content: ReactNode) => {
    Modal.info({
      title,
      content,
      onOk() {},
      zIndex: appZIndex.modal,
    });
  };

  // Show Confirm with navigation on new tab
  const showConfirm = (title: string, content: ReactNode, url: string) => {
    Modal.confirm({
      title,
      content,
      onOk() {
        window.open(url, "_blank");
      },
      onCancel() {
        console.log("Cancelled");
      },
      zIndex: appZIndex.modal,
    });
  };

  const loadCollections = async () => {
    let allCollections: IBookCollectionData[] = [];
    try {
      const catgeoryData = await userGetBookGroups();
      if (catgeoryData) {
        setCollections(catgeoryData?.data);
        allCollections = catgeoryData?.data;
      } else {
        setError("Collections not found");
      }
    } catch (err) {
      setError("Failed to load Collection data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCollections();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <BookSliderSkeleton title="Recommended Collections" />
      </div>
    );
  }

  if (error || !Collections) {
    return (
      <div className="p-4 text-center text-red-500">
        {error || "Collections not found"}
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-bold text-lg tracking-tight text-gray-900 dark:text-stone-100">
          Recommended Collections
        </h3>
        <div className="flex items-center gap-2">
          {showLeftArrow && (
            <button
              onClick={scrollLeft}
              className="w-8 h-8 flex items-center justify-center text-xs bg-gray-100 dark:bg-stone-700 rounded-full hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors text-gray-700 dark:text-stone-200"
              aria-label="Scroll left"
            >
              <LeftOutlined />
            </button>
          )}
          {showRightArrow && (
            <button
              onClick={scrollRight}
              className="w-8 h-8 flex items-center justify-center text-xs bg-gray-100 dark:bg-stone-700 rounded-full hover:bg-gray-200 dark:hover:bg-stone-600 transition-colors text-gray-700 dark:text-stone-200"
              aria-label="Scroll right"
            >
              <RightOutlined />
            </button>
          )}
        </div>
      </div>

      <div
        ref={sliderRef}
        className="flex overflow-x-auto pb-4 gap-4 scrollbar-hide"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {Collections.map((Collection, index: number) => {
          return (
            <Link
              key={index}
              to={`/users/book-collections-list/${Collection.id}/${Collection.title}`}
              className="flex-shrink-0 w-64 group"
            >
              <div className="relative h-72 rounded-2xl overflow-hidden shadow-md bg-gray-100 dark:bg-stone-700">
                <img
                  src={Collection.imageUrl || "/placeholder.svg"}
                  alt={Collection.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-semibold text-base line-clamp-2 mb-1 text-white">
                    {Collection.title}
                  </h3>
                  <p className="text-xs text-white/80 line-clamp-2">
                    {Collection.description}
                  </p>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default HomeCollectionCollections;
